/*
 * assets/b2b-fiscal-data.js
 * ---------------------------------------------------------------
 * Datos fiscales de empresa.
 *
 * Funciona con varias instancias a la vez (página del carrito y cajón
 * lateral) y las mantiene sincronizadas: lo que se escribe en una
 * aparece en la otra.
 *
 * Qué hace:
 *   1. Si el cliente ya tiene el CIF en su metacampo, lo copia a los
 *      atributos del carrito en silencio. No teclea nada.
 *   2. Si no, valida lo que escriba y lo guarda en el carrito.
 *   3. Bloquea TODAS las rutas al checkout mientras el CIF no sea válido:
 *      botón del carrito, botón del cajón, y pagos express (PayPal,
 *      Shop Pay), que no envían el formulario y se colarían.
 *
 * Atributos que viajan al pedido:
 *   es_empresa     'true' | 'false'
 *   razon_social   texto
 *   cif_nif        mayúsculas, sin espacios ni guiones
 * ---------------------------------------------------------------
 */
(function () {
  'use strict';

  var LETRAS_NIF = 'TRWAGMYFPDXBNJZSQVHLCKE';
  var LETRAS_CIF = 'JABCDEFGHI';

  /**
   * Valida NIF, NIE o CIF español comprobando el carácter de control.
   * @param {string} valor
   * @returns {{ok: boolean, tipo: string, motivo: string}}
   */
  function validarDocumento(valor) {
    if (!valor) return { ok: false, tipo: '', motivo: 'vacio' };

    var doc = valor.toUpperCase().replace(/[\s.\-_/]/g, '');

    // NIF (DNI): 8 dígitos + letra
    if (/^[0-9]{8}[A-Z]$/.test(doc)) {
      var nNif = parseInt(doc.slice(0, 8), 10);
      return {
        ok: LETRAS_NIF.charAt(nNif % 23) === doc.charAt(8),
        tipo: 'NIF',
        motivo: 'control'
      };
    }

    // NIE: X, Y o Z + 7 dígitos + letra
    if (/^[XYZ][0-9]{7}[A-Z]$/.test(doc)) {
      var prefijo = { X: '0', Y: '1', Z: '2' }[doc.charAt(0)];
      var nNie = parseInt(prefijo + doc.slice(1, 8), 10);
      return {
        ok: LETRAS_NIF.charAt(nNie % 23) === doc.charAt(8),
        tipo: 'NIE',
        motivo: 'control'
      };
    }

    // CIF: letra + 7 dígitos + control (dígito o letra)
    if (/^[ABCDEFGHJKLMNPQRSUVW][0-9]{7}[0-9A-J]$/.test(doc)) {
      var inicial = doc.charAt(0);
      var digitos = doc.slice(1, 8);
      var control = doc.charAt(8);
      var sumaPar = 0;
      var sumaImpar = 0;

      for (var i = 0; i < 7; i++) {
        var d = parseInt(digitos.charAt(i), 10);
        if (i % 2 === 0) {
          // Posiciones impares: se duplican y se suman las cifras.
          var x = d * 2;
          sumaImpar += Math.floor(x / 10) + (x % 10);
        } else {
          sumaPar += d;
        }
      }

      var digitoControl = (10 - ((sumaPar + sumaImpar) % 10)) % 10;
      var letraControl = LETRAS_CIF.charAt(digitoControl);
      var ok;

      if ('KPQRSNW'.indexOf(inicial) !== -1) {
        ok = control === letraControl;
      } else if ('ABEH'.indexOf(inicial) !== -1) {
        ok = control === String(digitoControl);
      } else {
        ok = control === String(digitoControl) || control === letraControl;
      }

      return { ok: ok, tipo: 'CIF', motivo: 'control' };
    }

    return { ok: false, tipo: '', motivo: 'formato' };
  }

  window.validarNifCif = validarDocumento;

  // -----------------------------------------------------------------

  function normalizar(v) {
    return (v || '').toUpperCase().replace(/[\s.\-_/]/g, '');
  }

  var temporizador = null;
  var guardando = false;

  function instancias() {
    return Array.prototype.slice.call(
      document.querySelectorAll('[data-b2b-fiscal]')
    );
  }

  function partes(raiz) {
    return {
      raiz: raiz,
      chk: raiz.querySelector('[data-b2b-toggle]'),
      campos: raiz.querySelector('[data-b2b-campos]'),
      resumen: raiz.querySelector('[data-b2b-resumen]'),
      edicion: raiz.querySelector('[data-b2b-edicion]'),
      cambiar: raiz.querySelector('[data-b2b-cambiar]'),
      razon: raiz.querySelector('[data-b2b-razon]'),
      cif: raiz.querySelector('[data-b2b-cif]'),
      errRazon: raiz.querySelector('[data-b2b-error-razon]'),
      errCif: raiz.querySelector('[data-b2b-error-cif]')
    };
  }

  /** Estado global, compartido por todas las instancias. */
  function estado() {
    var lista = instancias();
    if (!lista.length) return null;
    var p = partes(lista[0]);
    return {
      esEmpresa: p.chk.checked,
      razon: p.razon ? p.razon.value.trim() : '',
      cif: p.cif ? normalizar(p.cif.value) : ''
    };
  }

  /** Copia el estado a todas las instancias, para que carrito y cajón coincidan. */
  function propagar(origen) {
    var e = {
      esEmpresa: origen.chk.checked,
      razon: origen.razon ? origen.razon.value : '',
      cif: origen.cif ? origen.cif.value : ''
    };

    instancias().forEach(function (raiz) {
      var p = partes(raiz);
      if (p.raiz === origen.raiz) return;

      p.chk.checked = e.esEmpresa;
      p.campos.hidden = !e.esEmpresa;
      if (p.razon) p.razon.value = e.razon;
      if (p.cif) p.cif.value = e.cif;

      var resRazon = raiz.querySelector('[data-b2b-resumen-razon]');
      var resCif = raiz.querySelector('[data-b2b-resumen-cif]');
      if (resRazon) resRazon.textContent = e.razon;
      if (resCif) resCif.textContent = normalizar(e.cif);
    });
  }

  function mostrarError(p, campo, mensaje) {
    var destino = campo === p.cif ? p.errCif : p.errRazon;
    if (destino) destino.textContent = mensaje;
    campo.classList.add('b2b-fiscal__input--error');
    campo.setAttribute('aria-invalid', 'true');
  }

  function limpiarError(p, campo) {
    var destino = campo === p.cif ? p.errCif : p.errRazon;
    if (destino) destino.textContent = '';
    campo.classList.remove('b2b-fiscal__input--error');
    campo.removeAttribute('aria-invalid');
  }

  function validarInstancia(raiz, marcar) {
    var p = partes(raiz);
    if (!p.chk.checked) {
      if (p.razon) limpiarError(p, p.razon);
      if (p.cif) limpiarError(p, p.cif);
      return true;
    }

    var okRazon = true;
    var okCif = true;

    if (p.razon) {
      if (!p.razon.value.trim()) {
        okRazon = false;
        if (marcar) mostrarError(p, p.razon, 'Este dato es obligatorio para emitir la factura.');
      } else if (marcar) {
        limpiarError(p, p.razon);
      }
    }

    if (p.cif) {
      var res = validarDocumento(p.cif.value);
      okCif = res.ok;
      if (!res.ok && marcar) {
        mostrarError(
          p,
          p.cif,
          res.motivo === 'formato'
            ? 'El formato no corresponde a un CIF, NIF ni NIE español.'
            : 'El dígito de control no cuadra. Revisa el último carácter.'
        );
      } else if (marcar) {
        limpiarError(p, p.cif);
      }
    }

    return okRazon && okCif;
  }

  /** ¿Se puede pasar al checkout? */
  function esValido(marcar) {
    var lista = instancias();
    if (!lista.length) return true;
    // Todas las instancias tienen el mismo estado, basta validar la primera.
    // Si marcamos errores, los marcamos en todas para que se vean donde mire.
    var ok = validarInstancia(lista[0], marcar);
    if (marcar && lista.length > 1) {
      for (var i = 1; i < lista.length; i++) validarInstancia(lista[i], true);
    }
    return ok;
  }

  function guardar() {
    if (guardando) return;

    var e = estado();
    if (!e) return;

    var atributos = {
      es_empresa: e.esEmpresa ? 'true' : 'false',
      razon_social: e.esEmpresa ? e.razon : '',
      cif_nif: e.esEmpresa ? e.cif : ''
    };

    guardando = true;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: atributos })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('respuesta ' + r.status);
        instancias().forEach(function (raiz) {
          raiz.dataset.cifCarrito = atributos.cif_nif;
        });
      })
      .catch(function (err) {
        console.error('No se pudieron guardar los datos fiscales:', err);
      })
      .finally(function () {
        guardando = false;
        actualizarPagosExpress();
      });
  }

  /**
   * Los botones de pago express (PayPal, Shop Pay, Google Pay) no envían el
   * formulario: abren su propio flujo. No hay forma fiable de interceptarlos,
   * así que se ocultan mientras los datos fiscales estén incompletos.
   */
  function actualizarPagosExpress() {
    var e = estado();
    var bloquear = e && e.esEmpresa && !esValido(false);

    var contenedores = document.querySelectorAll(
      '.cart-dynamic-checkout-buttons, .additional-checkout-buttons'
    );

    Array.prototype.forEach.call(contenedores, function (c) {
      if (bloquear) {
        if (!c.dataset.b2bAvisoPuesto) {
          var aviso = document.createElement('p');
          aviso.className = 'b2b-fiscal__aviso-express';
          aviso.dataset.b2bAvisoExpress = '';
          aviso.textContent =
            'Los pagos rápidos no están disponibles con factura de empresa. ' +
            'Completa el CIF y usa el botón de pagar.';
          c.parentNode.insertBefore(aviso, c.nextSibling);
          c.dataset.b2bAvisoPuesto = '1';
        }
        c.hidden = true;
      } else {
        c.hidden = false;
        var aviso2 = c.parentNode
          ? c.parentNode.querySelector('[data-b2b-aviso-express]')
          : null;
        if (aviso2) aviso2.remove();
        delete c.dataset.b2bAvisoPuesto;
      }
    });
  }

  function enlazarInstancia(raiz) {
    if (raiz.dataset.b2bEnlazado) return;
    raiz.dataset.b2bEnlazado = '1';

    var p = partes(raiz);

    p.chk.addEventListener('change', function () {
      p.campos.hidden = !p.chk.checked;
      propagar(p);
      guardar();
      actualizarPagosExpress();
    });

    if (p.cambiar) {
      p.cambiar.addEventListener('click', function () {
        if (p.resumen) p.resumen.hidden = true;
        p.edicion.hidden = false;
        if (p.cif) p.cif.focus();
      });
    }

    [p.razon, p.cif].forEach(function (campo) {
      if (!campo) return;
      campo.addEventListener('input', function () {
        limpiarError(p, campo);
        propagar(p);
        clearTimeout(temporizador);
        temporizador = setTimeout(function () {
          guardar();
        }, 600);
      });
      campo.addEventListener('blur', function () {
        if (campo === p.cif) campo.value = normalizar(campo.value);
        validarInstancia(raiz, true);
        actualizarPagosExpress();
      });
    });
  }

  /**
   * Intercepta cualquier ruta al checkout: botón del carrito, botón del cajón
   * y envío de los formularios. Se engancha en fase de captura para llegar
   * antes que los manejadores del tema.
   */
  function bloquearCheckout() {
    document.addEventListener(
      'click',
      function (e) {
        var boton = e.target.closest
          ? e.target.closest('[name="checkout"], .cart-checkout-button, #checkout')
          : null;
        if (!boton) return;
        if (!instancias().length) return;

        if (!esValido(true)) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // Lleva al usuario al bloque visible más cercano al botón pulsado.
          var visibles = instancias().filter(function (r) {
            return r.offsetParent !== null;
          });
          var destino = visibles[0] || instancias()[0];
          destino.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var p = partes(destino);
          var malo = p.cif && p.cif.getAttribute('aria-invalid') ? p.cif : p.razon;
          if (malo) malo.focus();
        }
      },
      true
    );

    document.addEventListener(
      'submit',
      function (e) {
        if (!instancias().length) return;
        var form = e.target;
        if (!form.querySelector('[name="checkout"]') && form.id !== 'cart') return;
        if (!esValido(true)) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  function init() {
    instancias().forEach(enlazarInstancia);
    actualizarPagosExpress();

    // Si el cliente ya tiene el CIF guardado pero el carrito aún no lo lleva,
    // lo copiamos sin que tenga que hacer nada.
    var lista = instancias();
    if (lista.length) {
      var guardado = lista[0].dataset.cifGuardado || '';
      var enCarrito = lista[0].dataset.cifCarrito || '';
      if (guardado && guardado !== enCarrito) guardar();
    }
  }

  // El cajón lateral se repinta por AJAX al añadir productos, así que hay que
  // volver a enlazar las instancias que aparezcan después.
  var observador = new MutationObserver(function () {
    instancias().forEach(enlazarInstancia);
  });

  function arrancar() {
    init();
    bloquearCheckout();
    observador.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
})();
